// Imports ------------------------------------------------------------------->
import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";
import {compareCoupon, isHeroCoupon} from "../../utils/coupons";

import {Page} from "../flipp-page/Page";
import {FlyerListingItem, FlyerListingItemContext, TombstoneListingItem} from "../flipp-flyer-listing-item/FlyerListingItem";
import {CouponClipping} from "../flipp-coupon-clipping/CouponClipping";
import {EcomItemTile} from "../flipp-ecom-item-tile/EcomItemTile";
import {ItemLayout, ItemLayoutEntry} from "../flipp-item-layout/ItemLayout";

import {inject} from "../../services/di";
import {ConfigService} from "../../services/ConfigService";
import {EcomItem} from "../../services/EcomService";
import {SearchService, SearchModel} from "../../services/SearchService";
import {ShoppingListService} from "../../services/ShoppingListService";
import {LocationService} from "../../services/LocationService";
import {FlyerService} from "../../services/FlyerService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {EcomItemSummaryJSON} from "../../definitions/BackflippDefinitions";
import {Coupon} from "../../definitions/CouponDefinitions";
import {Flyer} from "../../definitions/FlyerDefinitions";
import {SearchMode} from "../../definitions/CommonDefinitions";
import {CouponSource, SourceAction} from "../../services/definitions/IAnalyticsService";
import MetadataService from "../../services/MetadataService";
import { isCrawler } from "../../utils/crawler";
import { SeoService } from "../../services/SeoService";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

interface SearchPageAttributes {
	query: string;
	sort?: string;
}

@element("flipp-search-page")
export class SearchPage extends Component<SearchPageAttributes, SearchModel> {
	@attr query: string;
	@attr sort: string = "all";

	render() {
		return (
			<Page show="flyers" search={this.query}>
				<SearchPageContent query={this.query} sort={this.sort} />
			</Page>
		);
	}
}

// flipp-search-page-content --------------------------------------------------------->

interface SearchPageContentAttributes {
	query: string;
	sort?: string;
	mode?: SearchMode;
}

@element("flipp-search-page-content")
export class SearchPageContent extends Component<SearchPageContentAttributes, SearchModel> {

	@inject location: LocationService;
	@inject flyerService: FlyerService;
	@inject searchService: SearchService;
	@inject analytics: AnalyticsService;
	@inject config: ConfigService;
	@inject shoppingList: ShoppingListService;
	@inject metadataService: MetadataService;
	@inject seoService: SeoService;

	@attr query: string;
	@attr sort: string = "all";
	@attr mode: SearchMode = "global";

	state = {
		showAllOnlineResults: false
	};

	onAttach() {
		window.addEventListener("flipp-couponclipped", this._handleClipping);
	}

	onDetach() {
		window.addEventListener("flipp-couponclipped", this._handleClipping);
	}

	prepare() {
		const result = this.searchService.hasSearchResult(this.query);
		if (result) {
			return result;
		}
		return this.searchService.getSearchResult(this.query);
	}

	renderLoading() {
		const isGlobalSearch = (this.mode === "global");

		return <div>
			<div class="topbar">
				<section class="sort-tabs" flex-horizontal>
					<button title={i18n("search_category_all")}
									aria-label={i18n("search_category_all")} disabled>
						<span class="tab">{i18n("search_category_all")}</span>
						<span class="quantity">  </span>
					</button>
					{isGlobalSearch ? <button title={i18n("search_category_flyers")}
									aria-label={i18n("search_category_flyers")} disabled>
						<div flex-horizontal>
							<span>{i18n("search_category_flyers")}</span>
							<span class="quantity">  </span>
						</div>
					</button> : null}
					<button title={i18n("search_category_items")}
									aria-label={i18n("search_category_items")} disabled>
						<div flex-horizontal>
							<span>{i18n("search_category_items")}</span>
							<span class="quantity">  </span>
						</div>
					</button>
					<button title={i18n("search_category_coupons")}
									aria-label={i18n("search_category_coupons")} disabled>
						<div flex-horizontal>
							<span>{i18n("search_category_coupons")}</span>
							<span class="quantity">  </span>
						</div>
					</button>
					<button title={i18n("search_category_online")}
									aria-label={i18n("search_category_online")} disabled>
						<div flex-horizontal>
							<span>{i18n("search_category_online")}</span>
						</div>
					</button>
				</section>
			</div>
			<div class="loading" flex-horizontal>
				<div>
					<div class="spinner"></div>
				</div>
			</div>
		</div>;
	}

	render([flyers, itemResults, coupons, ecomResults]: SearchModel) {
		const isGlobalSearch = (this.mode === "global");
		let additionalResults = null;

		const primaryEcomResults: EcomItemSummaryJSON[] = [];
		const additionalEcomResults: EcomItemSummaryJSON[] = [];
		const hiddenEcomResults: EcomItemSummaryJSON[] = [];

		if (Array.isArray(ecomResults) && ecomResults.length > 0) {
			ecomResults.forEach((ecomItem) => {
				if (ecomItem.display === "show") {
					primaryEcomResults.push(ecomItem);
				} else if (ecomItem.display === "link") {
					additionalEcomResults.push(ecomItem);
				} else { // display === "hide"
					hiddenEcomResults.push(ecomItem);
				}
			});
		}

		const numFlyers = flyers.length;
		const itemIds = itemResults.map((entry) => entry.item.flyer_item_id);
		const clippedItems = this.shoppingList.localList.flyerItemClippings;
		const clippedItemIds = clippedItems.filter((entry) => itemIds.indexOf(Number(entry.flyerItemId)) > -1)
												.map((entry) => Number(entry.flyerItemId));

		const clipped = itemResults.filter((entry) => clippedItemIds.indexOf(entry.item.flyer_item_id) > -1);
		const clippedFlyerItems = clipped.map((entry) => {
			return {
				flyer: entry.flyer,
				merchantLogo: entry.flyer.merchantLogo,
				itemId: `${entry.item.flyer_item_id}`,
				imageUrl: entry.item.clipping_image_url,
				top: entry.item.top,
				bottom: entry.item.bottom,
				left: entry.item.left,
				right: entry.item.right
			} as ItemLayoutEntry;
		});
		const numClippedItems = clippedFlyerItems.length;

		const flyerItems = itemResults
		.filter((entry) => entry.flyer !== null)
		.map((entry) => {
			return {
				flyer: entry.flyer,
				merchantLogo: entry.flyer.merchantLogo,
				itemId: `${entry.item.flyer_item_id}`,
				imageUrl: entry.item.clipping_image_url,
				top: entry.item.top,
				bottom: entry.item.bottom,
				left: entry.item.left,
				right: entry.item.right
			} as ItemLayoutEntry;
		});
		const numFlyerItems = flyerItems.length;

		const heroCoupons: Coupon[] = [];
		const rest: Coupon[] = [];

		// NOTE: We've seen a case where /search endpoint returns a valid couponId, however, /data endpoint
		// fails to produce a corresponding coupon, thus, returning 'null'.
		// In such a case, we must maintain the correct count for coupons.
		coupons.forEach((coupon) => {
			if (coupon && isHeroCoupon(coupon)) {
				heroCoupons.push(coupon);
			} else if (coupon) {
				rest.push(coupon);
			}
		});
		heroCoupons.sort(compareCoupon);
		rest.sort(compareCoupon);
		const numCoupons = heroCoupons.length + rest.length;

		const numPrimaryEcomItems = primaryEcomResults.length;
		const numAdditionalEcomItems = additionalEcomResults.length;
		const numEcomItems = Array.isArray(ecomResults) ? ecomResults.length : 0;

		const totalSearchResults = numFlyers + numFlyerItems + numCoupons + numEcomItems;
		if (totalSearchResults === 0) {
			this.metadataService.setRobotsNoIndexTag();
		}

		if (numAdditionalEcomItems > 0 && (this.sort === "all" || this.sort === "online")) {
			if (this.state.showAllOnlineResults) {
				additionalResults = (
					<section>
						{additionalEcomResults
							.map((ecomItem, idx) =>
								<div class="ecom-item-wrapper" onClick={this._trackSearchEcomItemHit.bind(this, ecomItem, idx)}>
									<EcomItemTile
										item={this._adapt(ecomItem)}
										secondary={true}
									/>
								</div>
							)
						}
					</section>
				);
			} else {
				additionalResults = (
					<button class="see-all-button" onClick={this._handleShowAllOnlineResults.bind(this, numAdditionalEcomItems)}>
						{icons.rubikCube}
						{i18n("see_all_online_results")}
					</button>
				);
			}
		}

		/* Calculate the width hint for the clippings item layout section */
		const pageWidth = document.documentElement.offsetWidth;
		const sideNavWidth = 200;
		const searchPagePadding = 70;
		const widthHint = pageWidth <= 800 ?
			pageWidth - searchPagePadding :
			pageWidth - sideNavWidth - searchPagePadding;

		console.log(numCoupons);
		console.log(heroCoupons);
		console.log(rest);

		this.seoService.insertLoadingMarker(70);
		return (
		<div>
			<div class="topbar">
				<section class="sort-tabs" flex-horizontal>
					<button title={i18n("search_category_all")}
									aria-label={i18n("search_category_all")}
									classList={{selected: this.sort === "all"}}
									onClick={this._handleSort.bind(this, totalSearchResults, "all")}>
						<span class="tab">{i18n("search_category_all")}</span>
						<span class="quantity">{totalSearchResults} </span>
					</button>
					{isGlobalSearch ? <button title={i18n("search_category_flyers")}
									aria-label={i18n("search_category_flyers")}
									classList={{selected: this.sort === "flyers"}}
									onClick={this._handleSort.bind(this, numFlyers, "flyers")}>
						<div flex-horizontal>
							<span>{i18n("search_category_flyers")}</span>
							<span class="quantity">{numFlyers} </span>
						</div>
					</button> : null}
					<button title={i18n("search_category_items")}
									aria-label={i18n("search_category_items")}
									classList={{selected: this.sort === "items"}}
									onClick={this._handleSort.bind(this, numFlyerItems, "items")}>
						<div flex-horizontal>
							<span>{i18n("search_category_items")}</span>
							<span class="quantity">{numFlyerItems} </span>
						</div>
					</button>
					<button title={i18n("search_category_coupons")}
									aria-label={i18n("search_category_coupons")}
									classList={{selected: this.sort === "coupons"}}
									onClick={this._handleSort.bind(this, numCoupons, "coupons")}>
						<div flex-horizontal>
							<span>{i18n("search_category_coupons")}</span>
							<span class="quantity">{numCoupons} </span>
						</div>
					</button>
					<button title={i18n("search_category_online")}
									aria-label={i18n("search_category_online")}
									classList={{selected: this.sort === "online"}}
									onClick={this._handleSort.bind(this, numEcomItems, "online")}>
						<div flex-horizontal>
							<span>{i18n("search_category_online")}</span>
							<span class="quantity">{numEcomItems}</span>
						</div>
					</button>
				</section>
			</div>
			<div class="content">
			{ isCrawler() ? <h1>{this.getH1()}</h1> : null}
			{ isGlobalSearch && numFlyers > 0 && (this.sort === "all" || this.sort === "flyers") ?
				<section class="flyers-section">
					<h2 class="search-section-heading">{this.getH2(i18n("flyers"))}</h2>
					<div>
						{flyers.map((flyer, idx) => flyer.isTombstone
							?
								<TombstoneListingItem
									merchantLogo={flyer.merchantLogo} />
							:
								<FlyerListingItem
									flyerId={flyer.id}
									context={FlyerListingItemContext.Search}
									onClick={this._trackFlyerHit.bind(this, flyer, idx)} />
						)}
					</div>
				</section>
			: null }

			{ !isGlobalSearch && numClippedItems > 0 && (this.sort === "all" || this.sort === "items") ?
				<section class="flyer-items-section">
					<div class="clippings">
						<h2 class="item">{this.getH2(i18n("your_clippings"))}</h2>
						<span>
							<img class="matchup-wrapper" src="https://f.wishabi.net/creative/Distribution/images/icons/matchupWrapper.svg" alt=""/>
						</span>
					</div>
					<ItemLayout
						items={clippedFlyerItems}
						merchantLogo={true}
						handleItemSelected={this._trackSearchItemHit}
						offsetHint={widthHint}
						sourceAction={SourceAction.ClippingsClick}
						/>
				</section>
			: null }

			{ numFlyerItems > 0 && (this.sort === "all" || this.sort === "items") ?
				<section class="flyer-items-section">
					<h2 class="item">{this.getH2(i18n("items"))}</h2>
					<ItemLayout
						items={flyerItems}
						merchantLogo={true}
						handleItemSelected={this._trackSearchItemHit}
						offsetHint={widthHint}
						sourceAction={SourceAction.SearchFlyerItemClick}
						query={this.query}
						/>
				</section>
			: null }

			{ numCoupons > 0 && (this.sort === "all" || this.sort === "coupons") ?
				<section class="coupons-section">
					<h2 class="search-section-heading">{this.getH2(i18n("coupons"))}</h2>
					<div>
						{heroCoupons.map((coupon, idx) =>
							<CouponClipping
								couponId={coupon.id}
								context="pop"
								source={isGlobalSearch ? CouponSource.SearchPage : CouponSource.ShoppingListSearch}
								onClick={this._trackCouponHit.bind(this, coupon, idx)}
							/>
						)}
						{rest.map((coupon, idx) =>
							<CouponClipping
								couponId={coupon.id}
								context="pop"
								source={isGlobalSearch ? CouponSource.SearchPage : CouponSource.ShoppingListSearch}
								onClick={this._trackCouponHit.bind(this, coupon, (heroCoupons.length + idx))}
							/>
						)}
					</div>
				</section>
			: null }

			{ numPrimaryEcomItems > 0 && (this.sort === "all" || this.sort === "online") ?
				<section>
					<h2 class="search-section-heading">{this.getH2(i18n("online_items"))}</h2>
					<div>
						{primaryEcomResults
							.map((ecomItem, idx) =>
								<div class="ecom-item-wrapper" onClick={this._trackSearchEcomItemHit.bind(this, ecomItem, idx)}>
									<EcomItemTile item={this._adapt(ecomItem)} />
								</div>
							)
						}
					</div>
				</section>
			: null }

			{additionalResults}
			{ totalSearchResults === 0 ?
				<span>
					<div class="empty-item-container" flex>
						<div flex-center>
							<img class="noSearchResults" src="https://f.wishabi.net/creative/Distribution/images/icons/noSearchResults.svg" alt=""/>
							<div>{i18n("no_search_results", { q: this.query || ""})}</div>
						</div>
					</div>
				</span>
			: null }
			</div>
		</div> );
	}

	// Helper function to adapt EcomItemSummaryJSON items into EcomItem.
	// At this point we have only EcomItemSummaryJSON (from /search endpoint),
	// however, EcomItemTile implementation expects fields such as `.expired` and
	// `.media` (see ecomUtil.thumbnail(item)), which exist only in the EcomItem
	// class. From the implementation point of view, it is a lot simpler and
	// easier to just adapt EcomItemSummaryJSON into a partially-filled EcomItem
	// with all of the fields from EcomItemSummaryJSON PLUS `.expired` and `.media`
	// fields set.
	// --------------------------------------------------------------------------
	private _adapt = (item: EcomItemSummaryJSON) => {
		return new EcomItem({
			ecom_item: {
				id: item.id,
				name: item.name,
				original_price: item.original_price,
				current_price: item.current_price,
				merchant: item.merchant,
				merchant_id: item.merchant_id,
				merchant_logo: item.merchant_logo,
				media: [{
					media_type: "image",
					media_name: "large",
					url: item.image_url
				}],
				expired: false
			}
		});
	}

	private _trackSearchItemHit = (item: ItemLayoutEntry, index: number) => {
		this.analytics.trackSearchItemClick(this.query, Number(item.itemId), item.flyer, index);
	}

	private _trackFlyerHit(flyer: Flyer, index: number) {
		this.analytics.trackSearchFlyerClick(this.query, flyer, index);
	}

	private _trackCouponHit(coupon: Coupon, index: number) {
		this.analytics.trackSearchCouponClick(this.query, coupon, index);
	}

	private _trackSearchEcomItemHit(ecomItem: EcomItemSummaryJSON, index: number) {
		this.analytics.trackSearchEcomItemClick(this.query, ecomItem.id, ecomItem.merchant_id, ecomItem.display !== "show", index);

		// NOTE: This is really redundant, because, unlike flyer items, ecom items are not browsed. i.e. the only way to OPEN an ecom item
		// is to SEARCH for it first. Thus, a search HIT (above) will provide all of the info.
		// This may change though if we introduce a separate page where a user can BROWSE ecom items.
		this.analytics.trackEcomItem(ecomItem.id, ecomItem.merchant_id, ecomItem.merchant, 0, ecomItem.display !== "show");
	}

	private _handleClipping = () => {
		this.invalidate();
	}

	private _handleSort = (count: number, sort: string, e: Event) => {
		e.preventDefault();
		this.sort = sort;
		this.analytics.trackSearchFiltering(count, sort);
	}

	private _handleShowAllOnlineResults = (count: number, e: Event) => {
		e.preventDefault();
		this.state.showAllOnlineResults = true;
		this.invalidate();

		this.analytics.trackAdditionalResultsClick(count);
	}

	private getH1 = () => {
		return i18n("search_page_h1", {
			city: this.location.city,
			q: this.query
		});
	}

	private getH2 = (itemType: string) => {
		return i18n("search_page_h2", {
			city: this.location.city,
			q: this.query,
			itemType
		});
	}
}
